import i18n from "../i18n";

export const DEFAULT_CURRENCY = "GBP";
// Customer setting key; the API reads the same key (CurrencyRules.SettingKey).
export const CURRENCY_SETTING_KEY = "Currency";
const STORAGE_KEY = "wilma:currency";

export function isCurrencyCode(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z]{3}$/.test(value.trim());
}

function readStoredCurrency(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isCurrencyCode(stored) ? stored : null;
  } catch {
    return null;
  }
}

// Module-level so plain helpers (PriceFormat, printed labels, CSV builders) can be called from
// anywhere without a hook. DashboardLayout sets it from the business's "Currency" setting before
// any page renders; the last known value is remembered so the first paint after a reload already
// uses it instead of flashing GBP.
let currencyCode: string = readStoredCurrency() ?? DEFAULT_CURRENCY;

export function setCurrencyCode(code: string | null | undefined): string {
  currencyCode = isCurrencyCode(code) ? code.trim().toUpperCase() : DEFAULT_CURRENCY;
  try {
    localStorage.setItem(STORAGE_KEY, currencyCode);
  } catch {
    // Storage can be unavailable (private mode); the in-memory value still applies.
  }
  return currencyCode;
}

export function getCurrencyCode(): string {
  return currencyCode;
}

// Number conventions follow the UI language; the currency follows the business.
export function currencyLocale(): string {
  return i18n.language === "pl" ? "pl-PL" : "en-GB";
}

export function formatPrice(
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions
): string {
  const amount = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat(currencyLocale(), {
    style: "currency",
    currency: currencyCode,
    ...options,
  }).format(typeof amount === "number" && Number.isFinite(amount) ? amount : 0);
}

// The symbol (or code) on its own, for input adornments and labels such as "Postage (£)".
export function currencySymbol(): string {
  const part = new Intl.NumberFormat(currencyLocale(), { style: "currency", currency: currencyCode })
    .formatToParts(0)
    .find((p) => p.type === "currency");
  return part?.value ?? currencyCode;
}
